import * as RxOp from "rxjs/operators";
import * as Rx from "rxjs";
import { createPaymentScheduleAttempt } from "../../graphql/Queries";
import { push } from "connected-react-router";
import {
  CREATE_PAYMENT_SCHEDULE,
  createPaymentScheduleSuccess,
  createPaymentScheduleFailure,
  accountsAlertBarAction,
  setUpAutopayAlertBarAction,
  addSchedule,
  CREATE_AUTOPAY_WITH_INLINE_DATA,
  UPSERT_RESOURCE,
  REQUEST_FAILURE,
  SUBMIT_OPERATIONS,
  CREDIT_CARD_RESOURCE,
  ACH_RESOURCE,
  ADDRESS_RESOURCE,
  ADDRESS_SETTING,
  CREDIT_CARD_KIND,
  submitChange,
  inlineAutopayStepUpdate,
  setAutoPaymentSelectedPaymentMethod,
  addCreditCardAlertBarAction,
  addAchAlertBarAction,
  addAddressAlertBarAction
} from "../../Profile.state";
import {
  addAlert,
  clearAlerts,
  replaceAlerts
} from "/components/alert-bar/AlertBar.state";
import {
  getConstituentID,
  getPaymentInstrumentId,
  getDetailedObligationAssociationId,
  getCustomerManagement
} from "../../Profile.selectors";
import { isInCustomerManagement, configureEndpoint } from "/util/router-utils";

const getCreatePaymentScheduleArgs = (key, selectors, state, action) => ({
  endpoint: selectors.getGraphqlServiceEndpoint(state),
  clientSlug: selectors.getClientSlug(state),
  authJwt: isInCustomerManagement
    ? selectors.getAdminAccessToken(state)
    : selectors.getAccessToken(state),
  schedule: {
    constituentID: getConstituentID(state),
    paymentInstrumentId: getPaymentInstrumentId(state[key]),
    subClientSlug:
      selectors
        .getSubclients(state)
        .find(subClient => subClient.path === action?.payload?.obligationSlug)
        ?.path ?? selectors.getSubclientPath(state),
    obligationAssociationId: getDetailedObligationAssociationId(state[key])
  }
});

const createPaymentScheduleEpic = (key, selectors) => (action$, state$) =>
  action$.ofType(CREATE_PAYMENT_SCHEDULE).pipe(
    RxOp.flatMap(action =>
      Rx.from(
        createPaymentScheduleAttempt(
          getCreatePaymentScheduleArgs(key, selectors, state$.value, action)
        )
      ).pipe(
        RxOp.mergeMap(response =>
          Rx.of(
            accountsAlertBarAction(clearAlerts()),
            createPaymentScheduleSuccess([response.createPaymentSchedule]),
            addSchedule(response.createPaymentSchedule),
            push(
              configureEndpoint(
                getCustomerManagement(state$.value[key]),
                "/profile/accounts"
              )
            ),
            accountsAlertBarAction(
              addAlert({
                heading: "Autopay Enabled",
                text: "Autopay has been enabled for your account.",
                variant: "success"
              })
            )
          )
        )
      )
    ),
    RxOp.catchError(error =>
      Rx.of(
        setUpAutopayAlertBarAction(clearAlerts()),
        createPaymentScheduleFailure(error),
        setUpAutopayAlertBarAction(
          addAlert({
            heading: "Payment Schedule Error",
            text:
              "An error occurred when enabling autopay for your account. Please try again.",
            variant: "error"
          })
        )
      )
    )
  );

// Sequences card/ACH then address creation via the existing per-resource
// epics. Schedule creation is a separate, caller-gated dispatch.
export const createAutopayWithInlineDataEpic = (key, _selectors) => (
  action$,
  state$
) =>
  action$.ofType(CREATE_AUTOPAY_WITH_INLINE_DATA).pipe(
    RxOp.mergeMap(action => {
      const { inlineCardData, inlineAddressData } = action.payload;

      const getOrchestration = () =>
        state$.value[key].settings.inlineAutopayOrchestration;

      const addressStep$ = () => {
        if (!inlineAddressData || getOrchestration().addressSaveSucceeded) {
          return Rx.EMPTY;
        }
        return Rx.concat(
          Rx.of(
            inlineAutopayStepUpdate("address", "attempted"),
            submitChange(ADDRESS_SETTING, SUBMIT_OPERATIONS.ADD, "", {
              noNavigation: true
            })
          ),
          Rx.race(
            action$.ofType(UPSERT_RESOURCE).pipe(
              RxOp.filter(a => a.payload.resourceType === ADDRESS_RESOURCE),
              RxOp.take(1),
              RxOp.map(() => ({ success: true }))
            ),
            action$.ofType(REQUEST_FAILURE).pipe(
              RxOp.filter(a => a.payload.settingName === ADDRESS_SETTING),
              RxOp.take(1),
              RxOp.map(a => ({ success: false, error: a.payload.error }))
            )
          ).pipe(
            RxOp.mergeMap(result =>
              result.success
                ? Rx.of(inlineAutopayStepUpdate("address", "succeeded"))
                : Rx.of(
                    inlineAutopayStepUpdate("address", "failed", result.error),
                    addAddressAlertBarAction(
                      replaceAlerts({
                        heading:
                          "There was an error saving your address. Check your address details and try again.",
                        variant: "error"
                      })
                    )
                  )
            )
          )
        );
      };

      const cardStep$ = () => {
        if (!inlineCardData || getOrchestration().cardSaveSucceeded) {
          return addressStep$();
        }
        const resource =
          inlineCardData.kind === CREDIT_CARD_KIND
            ? CREDIT_CARD_RESOURCE
            : ACH_RESOURCE;
        const alertBarAction =
          inlineCardData.kind === CREDIT_CARD_KIND
            ? addCreditCardAlertBarAction
            : addAchAlertBarAction;
        return Rx.concat(
          Rx.of(
            inlineAutopayStepUpdate("card", "attempted"),
            submitChange(resource, SUBMIT_OPERATIONS.ADD, "", {
              noNavigation: true
            })
          ),
          Rx.race(
            action$.ofType(UPSERT_RESOURCE).pipe(
              RxOp.filter(a => a.payload.resourceType === resource),
              RxOp.take(1),
              RxOp.map(a => ({ success: true, resource: a.payload.resource }))
            ),
            action$.ofType(REQUEST_FAILURE).pipe(
              RxOp.filter(a => a.payload.settingName === resource),
              RxOp.take(1),
              RxOp.map(a => ({ success: false, error: a.payload.error }))
            )
          ).pipe(
            RxOp.mergeMap(result => {
              if (!result.success) {
                return Rx.of(
                  inlineAutopayStepUpdate("card", "failed", result.error),
                  alertBarAction(
                    replaceAlerts({
                      heading:
                        "There was an error saving your payment method. Check your payment details and try again.",
                      variant: "error"
                    })
                  )
                );
              }
              return Rx.concat(
                Rx.of(
                  inlineAutopayStepUpdate("card", "succeeded"),
                  setAutoPaymentSelectedPaymentMethod(result.resource.id)
                ),
                addressStep$()
              );
            })
          )
        );
      };

      return cardStep$();
    })
  );

export default createPaymentScheduleEpic;
